/**
 * Pure helpers for the shared AccountLanguagesTable (ER-11416, account Languages
 * settings screen / ER-11373 Operating Languages management screen).
 *
 * Kept framework-free so unit tests can exercise status mapping, default/dubbing
 * detection and the row actions menu without mounting the SFC (mirrors the
 * approach used by the sibling JobFamiliesList / JobCategoriesList row helpers
 * and the Operating Country/Currency table composables).
 */

export const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "retired", label: "Retired" },
];

/** Row action values emitted by the table's overflow menu. */
export const ROW_ACTION_SET_DEFAULT = "setAsDefault";
export const ROW_ACTION_RETIRE = "retire";
export const ROW_ACTION_REACTIVATE = "reactivate";

const RETIRED_STATUSES = new Set(["retired", "inactive", "deactivated"]);

/**
 * An operating language is retired when the backend says so explicitly (status =
 * retired/inactive/deactivated) or when the legacy boolean `active: false` is
 * set. Anything else defaults to active so a partially hydrated row never renders
 * as read-only by accident.
 *
 * @param {{status?: string, active?: boolean}} language
 * @returns {"active"|"retired"}
 */
export function normalizeStatus(language) {
  const raw = language && typeof language === "object" ? language : {};
  if (raw.active === false) {
    return "retired";
  }
  const status = String(raw.status || "")
    .trim()
    .toLowerCase();
  return RETIRED_STATUSES.has(status) ? "retired" : "active";
}

/** Display name — supports a nested `language` object (mirrors OperatingCountryTable). */
export function languageName(language) {
  const raw = language && typeof language === "object" ? language : {};
  const nested = raw.language && typeof raw.language === "object" ? raw.language : {};
  return String(raw.name || nested.name || nested.display_name || "").trim();
}

/** ISO / locale code — used for stable keys and accessibility text. */
export function languageCode(language) {
  const raw = language && typeof language === "object" ? language : {};
  const nested = raw.language && typeof raw.language === "object" ? raw.language : {};
  return String(raw.code || raw.locale_code || nested.code || nested.locale_code || "").trim();
}

export function isDefaultLanguage(language) {
  const raw = language && typeof language === "object" ? language : {};
  return raw.is_default === true || raw.isDefault === true;
}

const DUBBING_BOOLEAN_KEYS = [
  "supports_ai_dubbing",
  "supports_dubbing",
  "dubbing_available",
  "has_dubbing",
];

/**
 * Whether dubbing content is available for the language. Accepts the same boolean
 * keys the AddOperatingLanguageDialog already understands (supports_dubbing /
 * supports_ai_dubbing), plus configured-row flags (dubbing_available / has_dubbing),
 * read from either the row itself or its nested `language` object.
 */
export function supportsDubbing(language) {
  const raw = language && typeof language === "object" ? language : {};
  const nested = raw.language && typeof raw.language === "object" ? raw.language : {};
  return DUBBING_BOOLEAN_KEYS.some((key) => raw[key] === true || nested[key] === true);
}

/**
 * Menu items for a row's Actions overflow (dots-horizontal) menu:
 *   - Active, not the default  -> Set as default · Retire (destructive, last)
 *   - Active default language  -> no menu (the account's default cannot be retired here)
 *   - Retired language         -> Reactivate (Rule 4 / Rule 13 restore)
 *
 * @returns {Array<{name: string, value: string, icon: string, destructive?: boolean}>}
 */
export function rowMenuItems(language) {
  const status = normalizeStatus(language);
  if (status !== "active") {
    return [
      {
        name: "Reactivate",
        value: ROW_ACTION_REACTIVATE,
        icon: "icons/dropdown/activate.svg",
        destructive: false,
      },
    ];
  }
  if (isDefaultLanguage(language)) {
    return [];
  }
  return [
    {
      name: "Set as default",
      value: ROW_ACTION_SET_DEFAULT,
      icon: "icons/dropdown/bookmark-check.svg",
      destructive: false,
    },
    {
      name: "Retire",
      value: ROW_ACTION_RETIRE,
      icon: "icons/dropdown/activate.svg",
      destructive: true,
    },
  ];
}

/** Human label for the row Status badge. */
export function statusLabel(status) {
  const normalized = status === "retired" ? "retired" : "active";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function buildRowView(language) {
  const raw = language && typeof language === "object" ? language : {};
  const status = normalizeStatus(raw);
  const retired = status === "retired";
  const isDefault = isDefaultLanguage(raw);

  return {
    id: raw.id ?? null,
    name: languageName(raw),
    code: languageCode(raw),
    status,
    retired,
    isDefault,
    dubbing: supportsDubbing(raw),
    unread: Number(raw.unread) > 0 ? Number(raw.unread) : 0,
    // Active non-default rows surface "Set as default" + "Retire"; retired rows
    // surface "Reactivate" so an admin can restore them (Rule 4 / Rule 13).
    menuItems: rowMenuItems(raw),
  };
}
